import React from "react";

/* The cruise line (PRD section 19): the only place the gradient lives. A 2px
   line, indigo to pink, drawn left to right over the completed stages only.
   `active` is the index of the stage in progress; earlier stages are done. */
export const STAGES = ["Listen", "Plan", "Create", "Check", "Approve", "Publish", "Learn"];

export const CruiseLine = ({ stages = STAGES, active = 0, labels = true, className = "" }) => {
  const last = stages.length - 1;
  const done = Math.max(0, Math.min(active, last));
  return (
    <div className={className} role="img"
      aria-label={`Pipeline: ${stages.slice(0, done + 1).join(", ")} done of ${stages.join(", ")}`}>
      <div className="relative h-3 mx-1.5">
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-ac-border" />
        <div className="cruise-line cruise-advance absolute left-0 top-1/2 -translate-y-1/2 transition-[width] duration-flow ease-ac"
          style={{ width: `${last ? (done / last) * 100 : 100}%` }} />
        {stages.map((s, i) => (
          <span key={s}
            className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full border transition-colors duration-base ${
              i <= done ? "border-transparent" : "bg-ac-bg border-ac-border"}`}
            style={{ left: `${last ? (i / last) * 100 : 0}%`,
              background: i <= done ? (i / Math.max(1, last) > 0.5 ? "#EC4899" : "#6366F1") : undefined }} />
        ))}
      </div>
      {labels && (
        <div className="relative h-4 mt-2 mx-1.5">
          {stages.map((s, i) => (
            <span key={s}
              className={`absolute -translate-x-1/2 whitespace-nowrap text-[11px] ${i <= done ? "text-ac-text" : "text-ac-muted"}`}
              style={{ left: `${last ? (i / last) * 100 : 0}%` }}>{s}</span>
          ))}
        </div>
      )}
    </div>
  );
};

/* One colour per status, used for pills, calendar dots and filters (PRD 23).
   Pink is reserved for "needs you". */
export const STATUS = {
  planned: { label: "Planned", cls: "bg-[#1A1D29] text-[#9CA3B4] border-[#2A2E3E]" },
  generating: { label: "Drafting", cls: "bg-[#1C1F2B] text-[#E5E7EB] border-[#30344A]" },
  draft: { label: "Drafting", cls: "bg-[#1C1F2B] text-[#E5E7EB] border-[#30344A]" },
  pending_approval: { label: "Needs you", cls: "bg-[#3B1029] text-[#F9A8D4] border-[#6B1D47]" },
  held: { label: "Held", cls: "bg-[#3B1029] text-[#F9A8D4] border-[#6B1D47]" },
  approved: { label: "Approved", cls: "bg-[#1E2150] text-[#C7D2FE] border-[#33378A]" },
  scheduled: { label: "Scheduled", cls: "bg-[#191C45] text-[#A5B4FC] border-[#2C3170]" },
  posted: { label: "Published", cls: "bg-[#0F2A1B] text-[#86EFAC] border-[#1A4D30]" },
  failed: { label: "Failed", cls: "bg-[#3A1414] text-[#FCA5A5] border-[#6B2121]" },
  rejected: { label: "Rejected", cls: "bg-[#1A1D29] text-[#9CA3B4] border-[#2A2E3E]" },
};

export const StatusPill = ({ status, held = false, className = "" }) => {
  const s = STATUS[held ? "held" : status] || STATUS.planned;
  return (
    <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium ${s.cls} ${className}`}>
      {s.label}
    </span>
  );
};

export default CruiseLine;
